'use client';

import { AdvancedAISettings } from '../../types/ai-settings';

interface AdvancedSettingsProps {
  settings: AdvancedAISettings;
  onChange: (settings: AdvancedAISettings) => void;
}

export function AdvancedSettings({ settings, onChange }: AdvancedSettingsProps) {
  const handleChange = <K extends keyof AdvancedAISettings>(
    key: K,
    value: AdvancedAISettings[K]
  ) => {
    onChange({ ...settings, [key]: value });
  };

  return (
    <div className="space-y-6">
      {/* Request Configuration */}
      <div className="bg-white/5 rounded-lg p-4">
        <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
          <span>⚡</span>
          Request Configuration
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-white/70 mb-2">
              Timeout (ms)
            </label>
            <input
              type="number"
              value={settings.timeout}
              onChange={(e) => handleChange('timeout', parseInt(e.target.value) || 30000)}
              min="5000"
              max="300000"
              step="1000"
              className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm text-white font-mono focus:border-indigo-500 focus:outline-none"
            />
            <p className="text-xs text-white/40 mt-1">
              Maximum time to wait for a response (5-300 seconds)
            </p>
          </div>

          <div>
            <label className="block text-xs text-white/70 mb-2">
              Retry Attempts
            </label>
            <input
              type="number"
              value={settings.retries}
              onChange={(e) => handleChange('retries', parseInt(e.target.value) || 3)}
              min="0"
              max="10"
              className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm text-white font-mono focus:border-indigo-500 focus:outline-none"
            />
            <p className="text-xs text-white/40 mt-1">
              Number of times to retry failed requests
            </p>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.cache}
                onChange={(e) => handleChange('cache', e.target.checked)}
                className="w-4 h-4 bg-white/10 border border-white/20 rounded text-indigo-500 focus:ring-indigo-500 focus:ring-offset-0"
              />
              <span className="text-sm text-white">Enable Response Caching</span>
            </label>
            <p className="text-xs text-white/40 mt-1 ml-6">
              Cache identical requests to reduce API costs
            </p>
          </div>
        </div>
      </div>

      {/* Generation Settings */}
      <div className="bg-white/5 rounded-lg p-4">
        <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
          <span>🎨</span>
          Generation Settings
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-white/70 mb-2">
              Context Window Override
            </label>
            <input
              type="number"
              value={settings.contextWindow || ''}
              onChange={(e) => handleChange('contextWindow', e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder="Use model default"
              min="1000"
              max="200000"
              step="1000"
              className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm text-white font-mono focus:border-indigo-500 focus:outline-none"
            />
            <p className="text-xs text-white/40 mt-1">
              Override the model's default context window size
            </p>
          </div>

          <div>
            <label className="block text-xs text-white/70 mb-2">
              System Prompt
            </label>
            <textarea
              value={settings.systemPrompt || ''}
              onChange={(e) => handleChange('systemPrompt', e.target.value || undefined)}
              placeholder="Enter a custom system prompt..."
              rows={4}
              className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm text-white font-mono focus:border-indigo-500 focus:outline-none resize-none"
            />
            <p className="text-xs text-white/40 mt-1">
              Default system prompt for all requests
            </p>
          </div>

          <div>
            <label className="block text-xs text-white/70 mb-2">
              Response Format
            </label>
            <select
              value={settings.responseFormat}
              onChange={(e) => handleChange('responseFormat', e.target.value as 'text' | 'json')}
              className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none"
            >
              <option value="text">Text</option>
              <option value="json">JSON</option>
            </select>
            <p className="text-xs text-white/40 mt-1">
              Preferred response format (model-dependent)
            </p>
          </div>

          <div>
            <label className="block text-xs text-white/70 mb-2">
              Stop Sequences
            </label>
            <input
              type="text"
              value={settings.stopSequences?.join(', ') || ''}
              onChange={(e) => {
                const sequences = e.target.value
                  .split(',')
                  .map(s => s.trim())
                  .filter(Boolean);
                handleChange('stopSequences', sequences.length > 0 ? sequences : undefined);
              }}
              placeholder="Enter comma-separated sequences..."
              className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm text-white font-mono focus:border-indigo-500 focus:outline-none"
            />
            <p className="text-xs text-white/40 mt-1">
              Sequences that will stop generation when encountered
            </p>
          </div>
        </div>
      </div>

      {/* Performance Optimization */}
      <div className="bg-white/5 rounded-lg p-4">
        <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
          <span>🚀</span>
          Performance Optimization
        </h3>

        <div className="space-y-3">
          <p className="text-xs text-white/60">
            These settings help optimize performance and reduce costs:
          </p>
          
          <ul className="space-y-2 text-xs text-white/50">
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-0.5">•</span>
              <span>Enable caching to avoid duplicate API calls</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-0.5">•</span>
              <span>Set appropriate timeouts to prevent hanging requests</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-0.5">•</span>
              <span>Use stop sequences to limit generation length</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-0.5">•</span>
              <span>Configure retry attempts for transient failures</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}